import { inject, provide } from 'vue';
import type { ComputedRef, InjectionKey, Ref, ShallowRef } from 'vue';
import type { TSheetHeight } from '@/components/pos-v2/ui/pos-ui-sheet.utils';
import type { IFlowScreenHandle } from '@/composition/store-setup/setup-flow-screen';
import type { TItemFilter } from '@/domain/menu-import/menu-draft';
import type {
  IAnswerQuestionDto,
  IDraftDelta,
  IDraftPatchResult,
  ILiveBody,
  IMenuDraft,
  IMenuImportPage,
  IMenuImportRead,
  IMenuImportSnapshot,
  IMenuImportTurn,
  IPublishResponse,
  TDraftOp,
  TMenuImportStatus,
} from '@/interfaces/IMenuImport';
import type { IUseDishPhotos } from './use-dish-photos';
import type { IUseMenuCapture } from './use-menu-capture';
import type { IUseMenuImportTurns } from './use-menu-import-turns';

/** Where the menu workspace runs: the setup flow or `/:id/menu/import`. */
export type TMenuImportEntry = 'setup' | 'import';
export type TMenuImportView = 'source' | 'workspace';
export type TMenuImportLoadState = 'idle' | 'loading' | 'ready' | 'error';
/**
 * `offline`: retrying a save after a network error, or waiting for the network
 * while the browser is offline; `failed`: retries exhausted while online,
 * waiting for «Reintentar».
 */
export type TMenuImportSaveState = 'saved' | 'saving' | 'offline' | 'failed';
export type TMenuWorkspaceMode = 'list' | 'queue';
export type TMenuSidePanelTab = 'chat' | 'original';

/** The parts of the snapshot that only change with the session itself (the rest has its own refs). */
export type TMenuImportSnapshotMeta = Pick<
  IMenuImportSnapshot,
  'sessionId' | 'restaurantId' | 'kind' | 'sourceRestaurantName' | 'limits'
>;

/** An owner decision of an update session (`POST :sid/live`). */
export type TLiveDecision = Pick<ILiveBody, 'link' | 'hide'>;

/** What «Publicar» ended with; the publish dialog maps it to its own states. */
export type TMenuPublishOutcome =
  | { result: 'published'; response: IPublishResponse }
  /** DRAFT_CHANGED: the snapshot was reloaded; LIVE_CHANGED: the menu is compared again. */
  | { result: 'changed' }
  /** UNRESOLVED: red dishes or blocking questions left. */
  | { result: 'unresolved'; itemIds: string[]; questionIds: string[] }
  | { result: 'nothingVisible' }
  | { result: 'failed' };

/**
 * The AI menu import session of one store (plan §10.4), built by
 * `useMenuImportSession`: server state (the draft is replaced, never
 * mutated, on every delta), workspace UI state, and the actions. Every draft
 * write goes through one serial queue.
 */
export interface IMenuImportSession {
  entry: TMenuImportEntry;
  loadState: Readonly<Ref<TMenuImportLoadState>>;
  snapshotMeta: Readonly<ShallowRef<TMenuImportSnapshotMeta | null>>;
  /** null until the session exists (it is created on the first source action). */
  sessionId: ComputedRef<string | null>;
  draft: Readonly<ShallowRef<IMenuDraft | null>>;
  rev: Readonly<Ref<number>>;
  /** null while the store has no session. */
  status: Readonly<Ref<TMenuImportStatus | null>>;
  pages: Readonly<ShallowRef<IMenuImportPage[]>>;
  /** The latest read. */
  read: Readonly<ShallowRef<IMenuImportRead | null>>;
  turns: Readonly<ShallowRef<IMenuImportTurn[]>>;
  canUndo: Readonly<Ref<boolean>>;
  aiEnabled: Readonly<Ref<boolean>>;
  /** Visible priced dishes the store already has (publish dialog; known before any session exists). */
  existingItems: Readonly<Ref<number>>;
  /** The menu is being compared again without an owner decision (the pre-publish check). */
  liveRefreshing: Readonly<Ref<boolean>>;
  saveState: Readonly<Ref<TMenuImportSaveState>>;
  /** Uploads, draft writes, answers, publish in flight: drives the leave guard. */
  pending: Readonly<Ref<number>>;
  busy: ComputedRef<boolean>;
  view: Ref<TMenuImportView>;
  /** The owner chose «Cargarlo a mano». */
  manual: Readonly<Ref<boolean>>;
  openItemId: Ref<string | null>;
  selectedItemId: Ref<string | null>;
  /** Remembered per device (`localStorage['menu-import-filter']`). */
  filter: Ref<TItemFilter>;
  mode: Ref<TMenuWorkspaceMode>;
  sidePanelTab: Ref<TMenuSidePanelTab>;
  sheetOpen: Ref<boolean>;
  /** Height of the phone sheet holding the side panel («Ver cambios» lowers it to `sm`). */
  sheetHeight: Ref<TSheetHeight>;
  viewerPageId: Ref<string | null>;
  /** The answered currency, else the store's: every price display and input prefix uses it. */
  displayCurrency: ComputedRef<string>;

  /** Loads the store's active session and picks the view; false when it failed. */
  load(): Promise<boolean>;
  /** Refetches the snapshot since the local rev (coalesced). */
  refresh(): Promise<void>;
  /** The session id, creating the session on first use; null (with a toast) when that failed. */
  ensureSession(): Promise<string | null>;
  /** pending + error toast (`menuImportErrorKey`, else `fallbackKey`); null when the task failed. */
  run<T>(task: () => Promise<T>, fallbackKey: string): Promise<T | null>;
  /** Counts one piece of work in `pending` until the returned release is called (idempotent). */
  hold(): () => void;
  /** Adds or replaces a page (an upload's answer) before its socket event arrives. */
  upsertPage(page: IMenuImportPage): void;
  removePage(pageId: string): void;
  /** Adds or replaces a chat turn (a send's 202 answer, a local proposal status). */
  upsertTurn(turn: IMenuImportTurn): void;

  /**
   * Serial queue: `PATCH …/draft` with a `clientMutationId` reused on its
   * retries; the answer's delta is already applied (a replayed answer has
   * been caught up through the snapshot). null: refused (already toasted).
   */
  mutate(ops: TDraftOp[]): Promise<IDraftPatchResult | null>;
  /** Serial queue, idempotent like `mutate`; the applied delta, or null. */
  answer(
    questionId: string,
    body: IAnswerQuestionDto
  ): Promise<IDraftDelta | null>;
  /** Serial queue, idempotent like `mutate`; the applied delta, or null. */
  apply(turnId: string, proposalId: string): Promise<IDraftDelta | null>;
  /**
   * «Deshacer»; shows «Listo, deshicimos el último cambio.» on success. With
   * `expectedRev` (a toast's «Deshacer»), only when the draft is still at
   * that rev when the undo goes out; otherwise «Ya no se puede deshacer».
   */
  undo(expectedRev?: number): Promise<boolean>;
  publish(): Promise<TMenuPublishOutcome>;
  /**
   * Update sessions: compares the draft with the store's menu again, after
   * `decision` when given (serial queue, idempotent). `silent`: no toast.
   * false when it failed (already toasted unless silent).
   */
  refreshLive(
    decision?: TLiveDecision,
    options?: { silent?: boolean }
  ): Promise<boolean>;
  /** «Reintentar» after the save retries ran out (any new write retries too). */
  retrySaves(): void;

  startRead(note: string | null): Promise<boolean>;
  cancelRead(): Promise<boolean>;
  /** «Intentar de nuevo»: the same pages, no re-upload. */
  retryRead(readNo: number): Promise<boolean>;
  copyFrom(sourceRestaurantId: number): Promise<boolean>;
  /** «Cargarlo a mano»: an empty workspace. */
  startManual(): Promise<boolean>;
}

/** What the menu flow provides to its screens: the session plus the chat, the capture tray and the dish photos. */
export interface IMenuImportContext extends IMenuImportSession {
  chat: IUseMenuImportTurns;
  capture: IUseMenuCapture;
  photos: IUseDishPhotos;
}

/**
 * What a page sees of `V2MenuFlow` through its template ref: the screen
 * handle plus the provided context (raw, so its refs stay refs), for the
 * top-bar actions the page renders outside the flow (`V2MenuContextBridge`).
 */
export interface IMenuFlowHandle extends IFlowScreenHandle {
  context: IMenuImportContext;
  /** New for every mounted flow: the bridge's key. */
  contextId: string;
}

/** The page's active screen is the menu flow (the only screen exposing a context). */
export function isMenuFlowHandle(
  handle: IFlowScreenHandle | null | undefined
): handle is IMenuFlowHandle {
  return !!handle && 'context' in handle && 'contextId' in handle;
}

export const MENU_IMPORT_KEY: InjectionKey<IMenuImportContext> = Symbol(
  'menu-import'
);

export function provideMenuImport(context: IMenuImportContext): void {
  provide(MENU_IMPORT_KEY, context);
}

/** Throws when no menu flow provides the context. */
export function useMenuImport(): IMenuImportContext {
  const context = inject(MENU_IMPORT_KEY, null);
  if (!context)
    throw new Error('useMenuImport() must be called inside the menu flow');
  return context;
}
